// =============================================================================
// Price Manager — faithful port of POS Admin frmChangePrice (a grid of active
// products with an editable Retail Price column + Save). Extended with sale-price
// editing and a bulk % / $ adjustment tool across the filtered set.
// =============================================================================
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Save, Search, Percent, DollarSign, Tag } from 'lucide-react'
import { searchProducts, updateProduct } from '../services/data'
import { PageHeader, Card, Spinner, Button, SearchInput } from '../components/shared/ui'
import { currency, productCode } from '../lib/format'
import { printProductTagsBatch, type TagSize } from '../lib/tags'
import type { Product } from '../types'
import { toast } from 'react-toastify'

interface Edited { retail: string; sale: string }

export default function PriceManager() {
  const [rows, setRows] = useState<Product[]>([])
  const [edits, setEdits] = useState<Record<string, Edited>>({})
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [bulkMode, setBulkMode] = useState<'percent' | 'amount'>('percent')
  const [bulkValue, setBulkValue] = useState('')

  const load = useCallback(async (term: string) => {
    setLoading(true)
    try {
      const data = await searchProducts(term, 200)
      setRows(data)
      setEdits(Object.fromEntries(data.map(p => [p.id, {
        retail: String(p.retail_price ?? ''),
        sale: String(p.sale_price ?? ''),
      }])))
    } catch (e) { console.error(e); toast.error('Failed to load products') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load('') }, [load])

  const setField = (id: string, key: keyof Edited, val: string) =>
    setEdits(prev => ({ ...prev, [id]: { ...prev[id], [key]: val } }))

  // Rows whose edited value differs from the stored value.
  const dirtyIds = useMemo(() => rows.filter(p => {
    const e = edits[p.id]; if (!e) return false
    return Number(e.retail || 0) !== Number(p.retail_price ?? 0) || Number(e.sale || 0) !== Number(p.sale_price ?? 0)
  }).map(p => p.id), [rows, edits])

  const applyBulk = () => {
    const v = parseFloat(bulkValue)
    if (!v || isNaN(v)) { toast.error('Enter a bulk adjustment value'); return }
    setEdits(prev => {
      const next = { ...prev }
      for (const p of rows) {
        const base = Number(prev[p.id]?.retail || p.retail_price || 0)
        const adjusted = bulkMode === 'percent' ? base * (1 + v / 100) : base + v
        next[p.id] = { ...prev[p.id], retail: (Math.round(adjusted * 100) / 100).toFixed(2) }
      }
      return next
    })
    toast.info(`Applied ${bulkMode === 'percent' ? `${v}%` : currency(v)} to ${rows.length} rows — review and Save.`)
  }

  const printLabels = (size: TagSize) => {
    if (rows.length === 0) { toast.info('No products to label'); return }
    const n = printProductTagsBatch(rows.map(p => ({
      name: p.name,
      price: Number(edits[p.id]?.retail || p.retail_price || 0),
      code: productCode(p),
      scan: (p.scan_number as string | null) ?? '',
    })), size)
    if (n) toast.success(`Queued ${n} label${n === 1 ? '' : 's'} for printing`)
  }

  const saveAll = async () => {
    if (dirtyIds.length === 0) { toast.info('No changes to save'); return }
    setSaving(true)
    try {
      await Promise.all(dirtyIds.map(id => updateProduct(id, {
        retail_price: Number(edits[id].retail) || 0,
        sale_price: edits[id].sale === '' ? null : Number(edits[id].sale) || 0,
      })))
      toast.success(`Saved ${dirtyIds.length} price change${dirtyIds.length === 1 ? '' : 's'}`)
      await load(search)
    } catch (e) { console.error(e); toast.error('Failed to save some prices') }
    finally { setSaving(false) }
  }

  return (
    <div>
      <PageHeader title="Price Manager" subtitle="Quick retail / sale price changes (legacy Change Price)"
        actions={<>
          <Button variant="secondary" onClick={() => printLabels('small')}><Tag size={16} /> Labels (Small)</Button>
          <Button variant="secondary" onClick={() => printLabels('large')}><Tag size={16} /> Labels (Large)</Button>
          <Button onClick={saveAll} disabled={saving || dirtyIds.length === 0}>
            <Save size={16} /> {saving ? 'Saving…' : `Save${dirtyIds.length ? ` (${dirtyIds.length})` : ''}`}
          </Button>
        </>} />

      <Card className="p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[220px]">
            <SearchInput value={search} onChange={setSearch} placeholder="Search products by name or code…" />
          </div>
          <Button variant="secondary" onClick={() => load(search)}><Search size={15} /> Search</Button>
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm text-gray-500">Bulk adjust retail:</span>
            <div className="flex rounded-lg overflow-hidden border border-gray-300">
              <button onClick={() => setBulkMode('percent')} className={`px-2 py-1.5 ${bulkMode === 'percent' ? 'bg-green-600 text-white' : 'bg-white text-gray-600'}`}><Percent size={14} /></button>
              <button onClick={() => setBulkMode('amount')} className={`px-2 py-1.5 ${bulkMode === 'amount' ? 'bg-green-600 text-white' : 'bg-white text-gray-600'}`}><DollarSign size={14} /></button>
            </div>
            <input type="number" step="0.01" value={bulkValue} onChange={e => setBulkValue(e.target.value)} placeholder={bulkMode === 'percent' ? '+10 / -5' : '+1.00'}
              className="w-24 border border-gray-300 rounded-lg px-2 py-1.5 text-right text-sm" />
            <Button variant="secondary" onClick={applyBulk}>Apply</Button>
          </div>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        {loading ? <div className="p-6"><Spinner label="Loading products…" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-gray-600">
                  <th className="text-left px-3 py-2.5 font-semibold">Code</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Description</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Type</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Cost</th>
                  <th className="text-right px-3 py-2.5 font-semibold w-32">Retail</th>
                  <th className="text-right px-3 py-2.5 font-semibold w-32">Sale</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-500">No products found.</td></tr>
                ) : rows.map(p => {
                  const e = edits[p.id]
                  const dirty = dirtyIds.includes(p.id)
                  return (
                    <tr key={p.id} className={`border-b border-gray-100 ${dirty ? 'bg-amber-50' : ''}`}>
                      <td className="px-3 py-1.5 font-medium text-gray-900">{productCode(p)}</td>
                      <td className="px-3 py-1.5 text-gray-700">{p.name}</td>
                      <td className="px-3 py-1.5 text-gray-500">{p.inventory_type || '—'}</td>
                      <td className="px-3 py-1.5 text-right text-gray-500">{currency(p.cost_price)}</td>
                      <td className="px-2 py-1.5">
                        <input type="number" step="0.01" value={e?.retail ?? ''} onChange={ev => setField(p.id, 'retail', ev.target.value)}
                          className="w-full text-right px-2 py-1 rounded border border-gray-200" />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="number" step="0.01" value={e?.sale ?? ''} onChange={ev => setField(p.id, 'sale', ev.target.value)}
                          className="w-full text-right px-2 py-1 rounded border border-gray-200" placeholder="—" />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
